import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useIntercom } from 'react-use-intercom';

import BenefitsCard from './sections/BenefitsCard/BenefitsCard';
import HealthCard from './sections/HealthCard/HealthCard';
import BdsSegment from '../../../shared/components/v3/BdsSegment/BdsSegment';
import BdsButton from '../../../shared/components/v3/Buttons/BdsButton/BdsButton';
import EmptyCard from '../../../shared/components/v3/Cards/EmptyCard/EmptyCard';
import InteractiveCard from '../../../shared/components/v3/Cards/InteractiveCard/InteractiveCard';
import PrimaryHero from '../../../shared/components/v3/Heros/PrimaryHero/PrimaryHero';
import BdsModal from '../../../shared/components/v3/Modals/BdsModal/BdsModal';
import Toast from '../../../shared/components/v3/Toast/Toast';
import { getChargeTypeBehavior } from '../../../shared/constants/chargeTypes';
import { CURRENCY_BUDDY, OUT_WHITE_ICON, ADD_ICON } from '../../../shared/constants/images';
import { DEFAULT_BDS_MODAL } from '../../../shared/constants/modal';
import { NO_PLANS_URL, PLAN_MODALS, UPGRADE_PLAN } from '../../../shared/constants/plan';
import {
  CLICK_ACTION,
  CONFIRM_UPSELLING,
  REQUEST_UPSELLING,
  SCREEN_MY_PLANS,
  SELECT_PLAN_DETAIL,
  UPSELLING_TYPES
} from '../../../shared/constants/tagManager';
import { DEFAULT_TOAST_DATA, GENERIC_ERROR_TOAST, UPSELL_REQUEST_SUCCESS_TOAST } from '../../../shared/constants/toast';
import { CompanyContext } from '../../../shared/context/company/companyContext';
import { PlansContext } from '../../../shared/context/plans/plansContext';
import { useCompanyBenefits } from '../../../shared/hooks/useCompanyBenefits/useCompanyBenefits';
import usePlanChargeType from '../../../shared/hooks/usePlanChargeType/usePlanChargeType';
import { IBdsModalContent } from '../../../shared/interfaces/bdsModal';
import { IToast } from '../../../shared/interfaces/toast';
import Company from '../../../shared/services/api/companies/companies';
import TagManagerService from '../../../shared/services/gtm/tagManagerService';
import { TRANSLATION_MY_PLAN } from '../../../shared/translations/plan';
import { UpsellingTypes } from '../../../shared/types/plan';

import './MyPlan.scss';

const MyPlan = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [modalType, setModalType] = useState<UpsellingTypes>();
  const [modalContent, setModalContent] = useState<IBdsModalContent>(DEFAULT_BDS_MODAL);
  const [toastData, setToastData] = useState<IToast>(DEFAULT_TOAST_DATA);
  const [openToast, setOpenToast] = useState(false);
  const [loadingUpsellRequest, setLoadingUpsellRequest] = useState(false);

  const { plans, loading, assingSelectedPlan, selectedPlan } = useContext(PlansContext);
  const { company } = useContext(CompanyContext);
  const { showNewMessages } = useIntercom();
  const { t } = useTranslation();

  const { allBenefitsState, refetch } = useCompanyBenefits({
    companyId: company.id,
    companyPlan: selectedPlan?.id
  });

  const { chargeType } = usePlanChargeType(company.id, selectedPlan?.id);
  const { priceLabelKey } = getChargeTypeBehavior(chargeType);

  const openBuddyChat = useCallback(() => {
    showNewMessages(t(TRANSLATION_MY_PLAN.buddyMessage));
  }, [showNewMessages, t]);

  const tabsItems = plans.map((plan) => plan.name);

  const hasTabs = useMemo(() => {
    return plans.length > 1 || loading;
  }, [plans, loading]);

  const hasPlans = useMemo(() => {
    return plans.length > 0 || loading;
  }, [plans, loading]);

  const onTabChange = (index: number) => {
    setTabIndex(index);
    assingSelectedPlan(plans[index]);
    const gtmParams = {
      ...SCREEN_MY_PLANS,
      tab: plans[index].name
    };
    TagManagerService.pushEvent(CLICK_ACTION, SELECT_PLAN_DETAIL, gtmParams);
  };

  const handleCloseToast = () => {
    setOpenToast(false);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const modalHandler = (type: UpsellingTypes) => {
    setModalType(type);
    setModalContent(PLAN_MODALS[type]);
    setOpenModal(true);
    TagManagerService.pushEvent(CLICK_ACTION, REQUEST_UPSELLING, { type: UPSELLING_TYPES[type] });
  };

  const sendUpsellingRequest = async () => {
    try {
      setLoadingUpsellRequest(true);
      const type = modalType ? UPSELLING_TYPES[modalType] : undefined;
      TagManagerService.pushEvent(CLICK_ACTION, CONFIRM_UPSELLING, { type });
      await Company.sendSalesMessage(company.id, UPGRADE_PLAN);
      setToastData(UPSELL_REQUEST_SUCCESS_TOAST);
    } catch {
      setToastData(GENERIC_ERROR_TOAST);
    } finally {
      setLoadingUpsellRequest(false);
      setOpenToast(true);
      setOpenModal(false);
    }
  };

  useEffect(() => {
    setTabIndex(plans.findIndex((plan) => plan.id === selectedPlan?.id));
  }, [plans, selectedPlan]);

  const fetchService = async () => {
    refetch(company.id);
  };

  return (
    <>
      <div className="my-plan-container flex flex-col" data-experience="new">
        <div className="my-plan-header">
          <PrimaryHero
            title={t(TRANSLATION_MY_PLAN.hero.title)}
            color="hero-my-plan"
            bottomContent={
              <div className="my-plan-hero-button">
                <BdsButton action={() => modalHandler(UPGRADE_PLAN)} variant="primary" size="m" iconLeft={ADD_ICON}>
                  {t(TRANSLATION_MY_PLAN.hero.button)}
                </BdsButton>
              </div>
            }
          />
          {hasTabs && (
            <div className="my-plan-tabs">
              {loading ? (
                <div className="flex flex-row gap-4 px-12">
                  <Skeleton className="tabs-skeleton" variant="rectangular" />
                </div>
              ) : (
                <div className="tabbar">
                  <BdsSegment items={tabsItems} activeIndex={tabIndex} action={onTabChange} fullWidth={true} />
                </div>
              )}
            </div>
          )}
        </div>
        <div className="mp-content-container">
          {hasPlans ? (
            <>
              <div className="mp-text-container">
                {loading ? (
                  <Skeleton className="text-skeleton" variant="rectangular" />
                ) : (
                  <div className="mp-title">{selectedPlan?.name}</div>
                )}
                {loading ? (
                  <Skeleton className="text-skeleton" variant="rectangular" />
                ) : (
                  <div className="flex flex-col gap-2">
                    <div className="mp-price">
                      {selectedPlan?.price} {selectedPlan?.currency}
                    </div>
                    {priceLabelKey && <div>{t(priceLabelKey)}</div>}
                  </div>
                )}
              </div>
              <HealthCard benefitsState={allBenefitsState} refetch={fetchService} modalHandler={modalHandler} />
              <BenefitsCard benefitsState={allBenefitsState} refetch={fetchService} modalHandler={modalHandler} />
              <div className="mp-support-card">
                <InteractiveCard
                  title={t(TRANSLATION_MY_PLAN.cards.support.title)}
                  description={t(TRANSLATION_MY_PLAN.cards.support.description)}
                  illustration={CURRENCY_BUDDY}
                  iconButton={OUT_WHITE_ICON}
                  action={openBuddyChat}
                />
              </div>
            </>
          ) : (
            <div className="mp-error-card-container">
              <EmptyCard
                primaryText={t(TRANSLATION_MY_PLAN.noPlans.title)}
                secondaryText={t(TRANSLATION_MY_PLAN.noPlans.description)}
                className="error-card-center"
                action={() => {
                  window.open(NO_PLANS_URL, '_blank');
                }}
                buttonVariant="primary"
                buttonText={t(TRANSLATION_MY_PLAN.noPlans.button)}
                center={true}
              />
            </div>
          )}
        </div>
      </div>
      <BdsModal
        open={openModal}
        title={t(modalContent.title as string)}
        description={t(modalContent.description as string)}
        icon={<modalContent.icon />}
        className="mp-custom-modal"
        primaryAction={{
          label: t(modalContent.primaryActionText ?? ''),
          onClick: sendUpsellingRequest
        }}
        loading={loadingUpsellRequest}
        onClose={handleCloseModal}
      />
      <Toast
        title={t(toastData.title)}
        description={t(toastData.description)}
        open={openToast}
        button={toastData.button}
        buttonText={t(toastData.buttonText)}
        buttonAction={handleCloseToast}
        handleClose={handleCloseToast}
        icon={toastData.icon}
      />
    </>
  );
};

export default MyPlan;
